import { useState } from "react";
import { Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/**
 * ShareButton — uses Web Share API on supported devices (iOS, Android, modern desktop),
 * falls back to copying the URL to clipboard.
 *
 * Props:
 *   title:    string shown in native share sheet (and clipboard toast)
 *   text:     short description for the share payload
 *   url:      URL to share — defaults to current page
 *   variant:  "primary" | "ghost" | "icon"   (visual style)
 *   className: extra classes
 *   testId:   data-testid override
 */
export const ShareButton = ({
  title,
  text,
  url,
  variant = "primary",
  className = "",
  testId = "share-button",
  onShare,
}) => {
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");

  const handleShare = async (e) => {
    e?.stopPropagation?.();
    if (typeof onShare === "function") {
      try { onShare(); } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("[share] onShare callback failed", e); }
    }
    const payload = { title, text, url: shareUrl };

    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (err) {
        // User cancelled — silent. Any other error → fall through to clipboard.
        if (err && err.name === "AbortError") return;
      }
    }

    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't share — please copy the URL manually");
    }
  };

  if (variant === "icon") {
    return (
      <button
        onClick={handleShare}
        aria-label="Share"
        data-testid={testId}
        className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm transition-colors ${className}`}
      >
        {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
      </button>
    );
  }

  if (variant === "ghost") {
    return (
      <Button
        onClick={handleShare}
        variant="outline"
        data-testid={testId}
        className={`rounded-full border-tbc-navy/20 text-tbc-navy hover:bg-tbc-navy hover:text-white ${className}`}
      >
        {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
        {copied ? "Copied" : "Share"}
      </Button>
    );
  }

  // primary
  return (
    <Button
      onClick={handleShare}
      data-testid={testId}
      className={`rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-medium ${className}`}
    >
      {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
      {copied ? "Copied!" : "Share"}
    </Button>
  );
};

export default ShareButton;
